import { useNavigate, useParams } from 'react-router-dom';
import { useEffect, useRef, useState } from 'react';
import { toast } from 'material-react-toastify';
import { Box, Button, IconButton, Typography } from '@mui/material';
import ArrowBackIcon from '@mui/icons-material/ArrowBack';
import PrintIcon from '@mui/icons-material/Print';
import LocalShippingIcon from '@mui/icons-material/LocalShipping';
import ShoppingBasketIcon from '@mui/icons-material/ShoppingBasket';
import { Order } from '../../../interfaces/Order/Order';
import { getOrder } from '../../../api/Order';
import { sharedStyles } from '../../../utils/Style';
import { Waiting } from '../../../components/Waiting/Waiting';
import { OrderDetailsPaper } from './OrderDetailsPaper';
import { OrderStockHistoryTable } from './OrderStockHistoryTable';
import { OrderStatus } from '../../../interfaces/Order/OrderStatus';
import { ShipmentImage } from '../../../interfaces/ShipmentImage/ShipmentImage';
import { getShipmentImages } from '../../../api/ShipmentImage';
import { ShipmentImages } from '../CollectOrderPage/ShipmentImages';

export const OrderDetailsPage = () => {
  const navigate = useNavigate();

  const { orderID: orderIDAsStr } = useParams<string>();

  const orderID = orderIDAsStr ? Number(orderIDAsStr) : -1;

  const [order, setOrder] = useState<Order | undefined>(undefined);
  const [shipmentImages, setShipmentImages] = useState<ShipmentImage[]>([]);
  const [loadingOrder, setLoadingOrder] = useState(false);
  const [loadingShipmentImages, setLoadingShipmentImages] = useState(false);

  const paperRef = useRef<HTMLDivElement>(null);

  const refreshOrder = () => {
    setLoadingOrder(true);
    getOrder(orderID)
      .then((o) => setOrder(o))
      .catch((err) => toast.error(err.message))
      .finally(() => setLoadingOrder(false));
  };

  const refreshShipmentImages = () => {
    setLoadingShipmentImages(true);
    getShipmentImages('internal', orderID)
      .then(setShipmentImages)
      .catch((err) => toast.error(err.message))
      .finally(() => setLoadingShipmentImages(false));
  };

  useEffect(() => {
    refreshOrder();
    refreshShipmentImages();
  }, [orderID]);

  const handlePrint = () => {
    if (paperRef.current) {
      const printContents = paperRef.current.outerHTML;
      const printWindow = window.open('', '_blank');
      if (printWindow) {
        const styles = Array.from(document.styleSheets)
          .map((styleSheet) => {
            try {
              return styleSheet.cssRules
                ? Array.from(styleSheet.cssRules)
                    .map((rule) => rule.cssText)
                    .join('\n')
                : '';
            } catch (error: any) {
              return '';
            }
          })
          .join('\n');

        printWindow.document.open();
        printWindow.document.write(`
        <html>
          <head>
            <title>Sipariş Detayları</title>
            <style>${styles}</style>
            <style>
              @media print {
                body {
                  margin: 0;
                  padding: 0;
                  -webkit-print-color-adjust: exact;
                }
                .no-print {
                  display: none !important;
                }
              }
            </style>
          </head>
          <body>${printContents}</body>
        </html>
      `);
        printWindow.document.close();
        printWindow.print();
      }
    }
  };

  const goBack = () => navigate('/sales/orders');

  if (loadingOrder) {
    return <Waiting open />;
  }

  if (!order) {
    return <Typography>Geçerli sipariş bulunamadı</Typography>;
  }

  return (
    <Box sx={{ ...sharedStyles.pageBody, gap: 1.5 }}>
      <Box
        sx={{
          display: 'flex',
          flexDirection: 'row',
          alignItems: 'center',
          flexWrap: 'wrap',
          mb: 3,
          gap: 1.5,
        }}
      >
        <IconButton onClick={goBack} sx={{ ...sharedStyles.backIcon }}>
          <ArrowBackIcon />
        </IconButton>
        <Typography variant='h5'>Sipariş Detayları</Typography>
        <Box sx={{ flexGrow: 1 }} />

        <Box sx={{ display: 'flex', flexDirection: 'row', gap: 1.5 }}>
          <Button
            variant='contained'
            onClick={handlePrint}
            startIcon={<PrintIcon />}
            sx={{ ...sharedStyles.buttonText }}
          >
            Yazdır
          </Button>

          <Button
            variant='contained'
            onClick={() => window.open(`/print-shipment/${order.id}`, '_blank')}
            startIcon={<LocalShippingIcon />}
            sx={{ ...sharedStyles.buttonText }}
          >
            Kargo Çıktısı
          </Button>

          <Button
            variant='contained'
            disabled={order.status !== OrderStatus.PROCESSING}
            onClick={() => navigate(`/sales/orders/${orderID}/collect`)}
            startIcon={<ShoppingBasketIcon />}
            sx={{ ...sharedStyles.buttonText }}
          >
            Hazırla
          </Button>
        </Box>
      </Box>

      <OrderDetailsPaper
        order={order}
        paperRef={paperRef}
        onUpdate={refreshOrder}
      />

      <OrderStockHistoryTable orderID={orderID} />

      {!!shipmentImages.length && (
        <ShipmentImages
          images={shipmentImages}
          loading={loadingShipmentImages}
        />
      )}
    </Box>
  );
};
